/**
 * Recommendation Letter helpers.
 *
 * Structure (We-voice, institutional):
 *   Salutation: To Whom It May Concern (fixed in cert.vue)
 *   ¶1 Student identity, project, achievement
 *   ¶2 Main contributions (member credits + high-activity modules)
 *   ¶3 Additional participation (lighter modules not covered above)
 *   ¶4 Summary evaluation
 *   Closing: separate block in cert.vue
 *
 * Never mention edit history, documented edits, system records, etc.
 */

import { extractMemberSectionFromTeamCredits, compressMemberCreditsForTranscript } from './competencyTranscript'
import { getAwardDisplay } from './certificateAwards'

const PRIMARY_MODULE_MIN_COUNT = 5
const BANNED_PHRASES = [
    /edit history/gi,
    /documented edits?/gi,
    /project development record/gi,
    /system record/gi,
    /corroborated by/gi,
    /across the project guide/gi,
    /sustained engagement from early research through prototype evaluation and final delivery/gi
]
const PROCEDURAL_MODULE_RE = /(member contributions?|team credits?|actual effects?|impact data|ethics statement|collaboration tools?|evaluate (?:the )?solutions?|grant of licen[cs]e|grant of liscence|group decision.making|identify the cause|prototype design|weakness|future improvement|reflection|project summary video|summary video|idea board|results? record|submission|appendix)/i

export function normalizeRecommendationLetter(payload, fallback) {
    const data = payload || {}
    const base = fallback || {}
    if (data.credential_status === 'issued') {
        const paragraphs = (data.letter_paragraphs || []).slice()
        return { generationStatus: 'issued', letterBody: paragraphs.join('\n\n'),
            isHtml: false, paragraphs, salutation: 'To Whom It May Concern,',
            studentName: data.student_name, projectTitle: data.project_title,
            closing: data.closing || '', usesInstitutionalWe: true }
    }

    const studentName = data.student_name || data.studentName || base.studentName || ''
    const projectTitle = data.project_title || data.projectTitle || base.projectTitle || ''
    const studentInfo = {
        student_givenName_pinyin: studentName.split(/\s+/)[0] || '',
        student_lastName_pinyin: studentName.split(/\s+/).slice(1).join(' ') || '',
        student_name: studentName
    }

    let studentCredits = data.student_credits || data.member_credits || ''
    if (!String(studentCredits).trim()) {
        const teamBlob = data.team_credits || data.teamCredits || base.memberCredits || base.teamCredits || ''
        if (teamBlob && studentName) {
            const extracted = extractMemberSectionFromTeamCredits(teamBlob, studentInfo)
            studentCredits = extracted.text || ''
        }
    }
    if (!String(studentCredits).trim()) {
        studentCredits = base.memberCredits || ''
    }

    const gitModules = normalizeGitModules(
        data.git_modules || data.gitModules || base.gitModules || [],
        data.git_summary || data.gitSummary || ''
    )

    const status = data.generation_status || data.status || 'ready'
    const suppliedClosing = data.closing || data.closing_line || ''
    const closing = suppliedClosing && !/without reservation/i.test(suppliedClosing) ? suppliedClosing :
        ('We are pleased to recognise ' + (studentName || 'this student') + '’s contribution to the work presented here.')

    const awardLabel = resolveAwardLabel(data, base)
    const trackName = data.track_name || data.trackName || base.trackName || ''
    const totalEarned = pickNumber(
        data.total_student_score,
        data.totalStudentScore,
        base.transcriptTotalEarned
    )
    const totalFull = pickNumber(
        data.total_full_score,
        data.totalFullScore,
        base.transcriptTotalFull
    )

    if (studentName) {
        const rebuilt = buildStructuredRecommendationLetter({
            studentName,
            projectTitle,
            trackName,
            studentCredits,
            gitModules,
            awardLabel,
            totalEarned,
            totalFull,
            closing
        })
        return {
            generationStatus: status || 'ready',
            letterBody: rebuilt.html,
            isHtml: true,
            paragraphs: rebuilt.paragraphs,
            salutation: 'To Whom It May Concern,',
            studentCredits,
            gitModules,
            studentName,
            projectTitle,
            closing,
            usesInstitutionalWe: true,
            looksLikeSourceDump: false
        }
    }

    return {
        generationStatus: status,
        letterBody: '',
        isHtml: true,
        paragraphs: [],
        salutation: 'To Whom It May Concern,',
        studentCredits,
        gitModules,
        studentName,
        projectTitle,
        closing,
        usesInstitutionalWe: false,
        looksLikeSourceDump: false
    }
}

export function buildStructuredRecommendationLetter(opts) {
    const studentName = (opts && opts.studentName) || 'this student'
    const firstName = studentName.split(/\s+/)[0] || studentName
    const projectTitle = (opts && opts.projectTitle) || ''
    const trackName = (opts && opts.trackName) || ''
    const credits = cleanCreditsText(opts && opts.studentCredits, studentName)
    const creditThemes = summarizeContributionThemes(credits)
    const gitModules = (opts && opts.gitModules) || []
    const awardLabel = (opts && opts.awardLabel) || ''
    const totalEarned = pickNumber(opts && opts.totalEarned)
    const totalFull = pickNumber(opts && opts.totalFull)

    const { primary, secondary } = classifyGitModules(gitModules, credits)
    const paragraphs = []

    // ¶1 — identity, project, achievement
    paragraphs.push(buildIntroParagraph({
        studentName,
        projectTitle,
        trackName,
        awardLabel,
        totalEarned,
        totalFull
    }))

    // ¶2 — main contributions (credits + high-activity modules)
    const para2 = buildMainContributionParagraph({
        studentName,
        firstName,
        credits,
        creditThemes,
        primaryModules: primary
    })
    if (para2) {
        paragraphs.push(para2)
    }

    // ¶3 — additional participation (lighter modules)
    const para3 = buildSupplementaryParagraph({
        studentName,
        firstName,
        secondaryModules: secondary,
        credits,
        primaryModules: primary
    })
    if (para3) {
        paragraphs.push(para3)
    }

    // ¶4 — summary evaluation
    paragraphs.push(buildSummaryParagraph({
        studentName,
        firstName,
        hasCredits: !!credits,
        hasGit: gitModules.length > 0
    }))

    const cleaned = paragraphs.map(stripBannedPhrases).filter(Boolean)
    const html = cleaned.map(p => '<p class="letter-para">' + escapeHtml(p) + '</p>').join('')
    return { paragraphs: cleaned, html }
}

/** @deprecated use buildStructuredRecommendationLetter */
export function buildWeVoiceLetter(opts) {
    return buildStructuredRecommendationLetter(opts)
}

function buildIntroParagraph(opts) {
    const studentName = opts.studentName
    const projectTitle = opts.projectTitle
    const trackName = opts.trackName
    const awardLabel = opts.awardLabel
    const totalEarned = opts.totalEarned
    const totalFull = opts.totalFull

    let text = 'We are pleased to recommend ' + studentName +
        ', a participant in the Next Idea Matters programme'

    if (projectTitle) {
        text += ', for work on the team project "' + projectTitle + '"'
    }

    if (trackName) {
        text += ' in the ' + trackName + ' track'
    }

    text += '.'

    const achievementParts = []
    if (awardLabel) {
        achievementParts.push(awardSentence(awardLabel, studentName))
    }
    if (!awardLabel && totalFull > 0 && totalEarned >= 0) {
        achievementParts.push('the project was completed through the formal NIM assessment process')
    }

    if (achievementParts.length === 1) {
        text += ' ' + achievementParts[0] + '.'
    } else if (achievementParts.length === 2) {
        text += ' ' + achievementParts[0] + ', and ' + achievementParts[1] + '.'
    }

    return text
}

function buildMainContributionParagraph(opts) {
    const contribution = compressMemberCreditsForTranscript(opts.credits)
        .replace(/(^|\. )(Used|Coordinated|Organised|Examined|Supported|Communicated|Contributed)\b/g, (match, prefix, verb) => prefix + (prefix ? 'They' : opts.firstName) + ' ' + verb.toLowerCase())
    const skills = narrativePrimaryGit(opts.firstName, opts.primaryModules || [])
    return [contribution, skills].filter(Boolean).join(' ')
}

function buildSupplementaryParagraph(opts) {
    const firstName = opts.firstName
    const secondaryModules = (opts.secondaryModules || []).filter(mod => {
        return !moduleReferencedInText(mod.name, opts.credits) &&
            !(opts.primaryModules || []).some(p => p.name === mod.name)
    })
    if (!secondaryModules.length) {
        return ''
    }
    const primarySkills = new Set(moduleCompetencies(opts.primaryModules || []).map(skill => String(skill).toLowerCase()))
    return narrativeSecondaryGit(firstName, secondaryModules, primarySkills)
}

function buildSummaryParagraph(opts) {
    if (!opts.hasCredits && !opts.hasGit) return ''
    return '' // The specific contribution paragraphs already provide the basis for recommendation.
}

function classifyGitModules(modules, creditsText) {
    const list = (modules || []).slice().sort((a, b) => b.count - a.count)
    if (!list.length) {
        return { primary: [], secondary: [] }
    }
    const total = list.reduce((sum, row) => sum + (row.count || 0), 0)
    const primary = []
    const secondary = []

    list.forEach(mod => {
        const count = mod.count || 0
        const share = total > 0 ? count / total : 0
        const mentioned = moduleReferencedInText(mod.name, creditsText)
        const isPrimary = count >= PRIMARY_MODULE_MIN_COUNT || share >= 0.12 || mentioned
        if (isPrimary) {
            primary.push(mod)
        } else if (count > 0) {
            secondary.push(mod)
        }
    })

    if (!primary.length && list.length) {
        primary.push(list[0])
        secondary.splice(0, 1)
    }

    return { primary, secondary }
}

function moduleCompetencies(modules) {
    return [...new Set((modules || []).reduce((all, module) => all.concat(module.competencies || []), []))].filter(Boolean).slice(0, 3)
}
function narrativePrimaryGit(firstName, modules) {
    const skills = moduleCompetencies(modules)
    if (!skills.length) return ''
    return 'These contributions gave ' + firstName + ' a practical context for ' + formatEnglishList(skills.map(skill => String(skill).toLowerCase().replace(/&/g, 'and'))) + '.'
}

function participationSpanPhrase(modules) {
    let earliest = null
    let latest = null
    ;(modules || []).forEach(mod => {
        const first = parseTimestamp(mod.firstAt)
        const last = parseTimestamp(mod.lastAt)
        if (first != null) {
            earliest = earliest == null ? first : Math.min(earliest, first)
        }
        if (last != null) {
            latest = latest == null ? last : Math.max(latest, last)
        }
    })
    if (earliest == null || latest == null || latest <= earliest) {
        return 'throughout the project timeline'
    }
    const days = Math.round((latest - earliest) / 86400000)
    if (days >= 28) {
        return 'over an extended period of the project cycle, from early development through final delivery'
    }
    if (days >= 14) {
        return 'from early drafting through final refinement'
    }
    return 'throughout the project timeline'
}

function parseTimestamp(value) {
    if (value == null || value === '') {
        return null
    }
    const n = Number(value)
    if (isNaN(n) || n <= 0) {
        return null
    }
    // Unix seconds vs milliseconds
    return n < 1e12 ? n * 1000 : n
}

function narrativeSecondaryGit(firstName, modules, excluded = new Set()) {
    const skills = moduleCompetencies(modules).filter(skill => !excluded.has(String(skill).toLowerCase()))
    return skills.length ? 'Further contributions gave ' + firstName + ' opportunities to apply ' + formatEnglishList(skills.map(skill => String(skill).toLowerCase().replace(/&/g, 'and'))) + ' in support of the team’s work.' : ''
}

function normalizeGitModules(rawModules, legacySummary) {
    if (Array.isArray(rawModules) && rawModules.length) {
        return rawModules.map(row => ({
            name: String(row.name || row.block_name || row.module || '').trim(),
            competencies: Array.isArray(row.competencies) ? row.competencies : [],
            count: Number(row.count) || 0,
            firstAt: row.first_at || row.firstAt || null,
            lastAt: row.last_at || row.lastAt || null
        })).filter(row => row.name && row.count > 0 && !PROCEDURAL_MODULE_RE.test(row.name))
    }
    return parseLegacyGitSummary(legacySummary).filter(row => row.name && !PROCEDURAL_MODULE_RE.test(row.name))
}

function parseLegacyGitSummary(summary) {
    const text = String(summary || '')
    const match = text.match(/including\s+(.+?)\.?$/i)
    if (!match) {
        return []
    }
    return match[1].split(',').map(part => ({
        name: part.trim(),
        count: PRIMARY_MODULE_MIN_COUNT,
        firstAt: null,
        lastAt: null
    })).filter(row => row.name)
}

function moduleReferencedInText(moduleName, creditsText) {
    const credits = String(creditsText || '').toLowerCase()
    const name = String(moduleName || '').toLowerCase()
    if (!credits || !name) {
        return false
    }
    if (credits.indexOf(name) >= 0) {
        return true
    }
    const tokens = name.split(/[^a-z0-9]+/i).filter(token => token.length > 3)
    return tokens.some(token => credits.indexOf(token) >= 0)
}

function cleanCreditsText(value, studentName) {
    let text = stripSourceLabels(value)
    if (!text) {
        return ''
    }
    const name = String(studentName || '').trim()
    if (name) {
        text = text.replace(new RegExp('^' + escapeRegExp(name) + '\\s*', 'i'), '')
    }
    return text
}

function summarizeContributionThemes(value) {
    const text = String(value || '').toLowerCase()
    if (!text) {
        return ''
    }
    const themes = []
    const rules = [
        { label: 'user research and problem framing', re: /research|survey|interview|persona|stakeholder|problem|need|pain point/ },
        { label: 'data analysis and evidence evaluation', re: /data|analysis|metric|evidence|impact|result|evaluation|testing|experiment/ },
        { label: 'solution design and prototype development', re: /design|prototype|wireframe|solution|feature|model|build|develop|implementation/ },
        { label: 'project planning and team coordination', re: /plan|timeline|coordinate|leader|manage|schedule|meeting|collaborat|team/ },
        { label: 'communication and final presentation', re: /present|presentation|report|writing|communicat|pitch|visual|slide|document/ }
    ]
    rules.forEach(rule => {
        if (rule.re.test(text)) {
            themes.push(rule.label)
        }
    })
    if (!themes.length) {
        return ''
    }
    return formatEnglishList(themes.slice(0, 3))
}

function resolveAwardLabel(data, base) {
    const fromPayload = data.award_label || data.awardLabel ||
        (data.award && (data.award.award_lable || data.award.award_label || data.award.award_name))
    if (fromPayload) {
        return String(fromPayload).trim()
    }
    const award = base.primaryAward || (base.awards && base.awards[0])
    if (award) {
        return getAwardDisplay(award).name || ''
    }
    return ''
}

function awardSentence(label) {
    return 'The team project received ' + label + ' recognition'
}

function stripBannedPhrases(value) {
    let text = String(value || '')
    BANNED_PHRASES.forEach(pattern => {
        text = text.replace(pattern, ' ')
    })
    return text.replace(/\s+/g, ' ').trim()
}

function stripSourceLabels(value) {
    return String(value || '')
        .replace(/^\s*Contribution:\s*/i, '')
        .replace(/^\s*Project development:\s*/i, '')
        .replace(/^\s*Git History\s*Summary:\s*/i, '')
        .replace(/^\s*Team Credits:\s*/i, '')
        .trim()
}

function escapeHtml(value) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
}

function escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function formatEnglishList(items) {
    const list = (items || []).filter(Boolean)
    if (!list.length) {
        return ''
    }
    if (list.length === 1) {
        return list[0]
    }
    if (list.length === 2) {
        return list[0] + ' and ' + list[1]
    }
    return list.slice(0, -1).join(', ') + ', and ' + list[list.length - 1]
}

function pickNumber() {
    for (let i = 0; i < arguments.length; i++) {
        const n = Number(arguments[i])
        if (!isNaN(n) && arguments[i] !== '' && arguments[i] != null) {
            return n
        }
    }
    return null
}

export function splitLetterParagraphs(body) {
    const text = String(body || '').replace(/\r\n/g, '\n').trim()
    if (!text) {
        return []
    }
    return text
        .split(/\n\s*\n/)
        .map(part => part.replace(/\n/g, ' ').trim())
        .filter(Boolean)
}
